import { useState, useEffect, useMemo } from 'react'
import Select from 'react-select'
import { FaTrashAlt, FaPlus } from 'react-icons/fa'

import api from '../../../utils/api'
import Input from '../../../shared/components/form/input'
import Checkbox from '../../../shared/components/form/checkbox'
import Button from '../../../shared/components/Button/Button'
import Modal from '../../../shared/modal/Modal'
import FormLayout from '../../../shared/components/FormLayout/FormLayout'
import selectStyles from '../../../shared/css/SelectStyle'
import buildPurchaseSummary from '../../../shared/functions/buildPurchaseSummary'
import findClothPurchaseRule from '../../../shared/functions/findClothPurchaseRule'
import useFlashMessage from '../../../hooks/useFlashMessage'
import NewProvider from '../../provider/NewProvider'
import NumberToMoney from '../../../shared/functions/NumberToMoney'

import './PurchaseOrder.css'

const MANUAL_TYPE_OPTIONS = [
    { value: 'cloth', label: 'Tecido' },
    { value: 'aviamento', label: 'Aviamento' },
]

// Mesma chave usada em StockController.getSaldo — precisa bater exatamente pra casar o saldo
// disponível com cada grupo do resumo de compra.
function saldoKey(materialType, materialId, providerId, unitValue) {
    return `${materialType}_${materialId}_${providerId || '-'}_${unitValue}`
}

// editingPO: quando informado (vindo de PurchaseOrderList, populado com provider/items/reservedLots.lot),
// o formulário abre em modo de edição em vez de criação — só é aceito enquanto status === 'aguardando'
// (backend recusa depois do recebimento, o lote de estoque já foi gerado nesse ponto).
function NewPurchaseOrder({ order, editingPO, btCancelAction, handleOnSuccess, myPermissionProviderCreate }) {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()

    const [modalProvider, setModalProvider] = useState(false)

    const [providers, setProviders] = useState([])
    const [cloths, setCloths] = useState([])
    const [aviamentos, setAviamentos] = useState([])
    const [stockSaldoMap, setStockSaldoMap] = useState({})
    const [load, setLoad] = useState(false)

    const [providerSelected, setProviderSelected] = useState(editingPO ? editingPO.provider : null)
    const [expectedArrival, setExpectedArrival] = useState(editingPO?.expectedArrival ? editingPO.expectedArrival.substring(0, 10) : '')
    const [obs, setObs] = useState(editingPO?.obs || '')
    // Condição de pagamento da Conta a Pagar gerada automaticamente com este Pedido de Compra
    // (Financeiro) — só usada na criação, não editável aqui numa edição posterior do PC (o
    // lançamento financeiro, se já tiver parcela paga, precisa ser ajustado direto na tela de
    // Contas a Pagar).
    const [installments, setInstallments] = useState('1')
    const [rows, setRows] = useState([])

    // Item adicionado na mão, fora da necessidade calculada do pedido (buildPurchaseSummary) —
    // ex: algo que a pessoa percebe que precisa comprar mas não fazia parte do cálculo original.
    const [addItemModal, setAddItemModal] = useState(false)
    const [manualType, setManualType] = useState('cloth')
    const [manualCloth, setManualCloth] = useState(null)
    const [manualAviamento, setManualAviamento] = useState(null)
    const [manualQuantity, setManualQuantity] = useState('')
    const [manualUnitPrice, setManualUnitPrice] = useState('')

    useEffect(() => {
        Promise.all([
            api.get('/product/getInfoSelect', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
            api.get('/stock/saldo', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
            api.get('/aviamento/getall', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
            api.get(`/purchase/order/getByOrder/${order._id}`, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
        ]).then(([infoRes, saldoRes, aviamentoRes, poRes]) => {
            setProviders(infoRes.data.providers || [])
            setCloths(infoRes.data.cloths || [])
            setAviamentos(aviamentoRes.data.aviamentos || [])

            const map = {}
            ;(saldoRes.data.saldo || []).forEach((s) => {
                const materialId = s.materialType === 'aviamento' ? s.aviamento?._id : s.cloth?._id
                if (!materialId) return
                map[saldoKey(s.materialType, materialId, s.provider?._id, s.unit.value)] = s.availableQuantity
            })
            setStockSaldoMap(map)

            // Quanto desse pedido já está comprometido em OUTRO Pedido de Compra (aguardando ou já
            // recebido) — a parte já reservada em Estoque (source 'estoque'/'compra') já reduz o
            // "disponível" acima, mas a parte que ainda vai ser comprada (não virou lote ainda) só
            // existe aqui, no item do outro PC. Sem isso, abrir "Novo Pedido de Compra" de novo pro
            // mesmo pedido voltaria a sugerir a necessidade bruta inteira, como se nada tivesse sido
            // comprado ainda.
            const otherCommittedMap = {}
            ;((poRes.data.purchaseOrders || [])).forEach((po) => {
                if (po.status === 'cancelado') return
                if (editingPO && String(po._id) === String(editingPO._id)) return
                ;(po.items || []).forEach((it) => {
                    const materialId = it.materialType === 'aviamento' ? (it.aviamento?._id || it.aviamento) : (it.cloth?._id || it.cloth)
                    if (!materialId) return
                    const key = `${it.materialType}_${materialId}`
                    otherCommittedMap[key] = (otherCommittedMap[key] || 0) + it.quantity
                })
            })

            // Modo de edição: soma quanto este próprio pedido já tem reservado por material (pra
            // devolver como "disponível" — senão a própria reserva do pedido pareceria consumida
            // por outro pedido) e recupera os itens já lançados, pra pré-preencher em vez de
            // assumir tudo "a comprar" do zero.
            const ownReservedMap = {}
            const existingItemsMap = {}
            if (editingPO) {
                ;(editingPO.reservedLots || []).forEach((rl) => {
                    const lot = rl.lot
                    if (!lot) return
                    const materialId = lot.materialType === 'aviamento' ? lot.aviamento?._id : lot.cloth?._id
                    if (!materialId) return
                    const key = saldoKey(lot.materialType, materialId, lot.provider?._id, lot.unit?.value)
                    ownReservedMap[key] = (ownReservedMap[key] || 0) + rl.quantity
                })
                ;(editingPO.items || []).forEach((it) => {
                    const materialId = it.materialType === 'aviamento' ? (it.aviamento?._id || it.aviamento) : (it.cloth?._id || it.cloth)
                    if (!materialId) return
                    existingItemsMap[`${it.materialType}_${materialId}`] = it
                })
            }

            const consumedKeys = new Set()
            const gross = buildPurchaseSummary(order)
            const grossRows = gross.map((g) => {
                const materialId = g.type === 'aviamento' ? g.aviamentoId : g.clothId
                consumedKeys.add(`${g.type}_${materialId}`)
                const key = saldoKey(g.type, materialId, g.providerId, g.unit?.value)
                const ownReserved = ownReservedMap[key] || 0
                const available = (map[key] || 0) + ownReserved
                const existingItem = existingItemsMap[`${g.type}_${materialId}`]
                const stockUse = Math.min(available, ownReserved)
                const otherCommitted = otherCommittedMap[`${g.type}_${materialId}`] || 0
                const remainingGross = Math.max(0, g.total - otherCommitted)
                return {
                    ...g,
                    grossQuantity: remainingGross,
                    originalGrossQuantity: g.total,
                    otherCommitted,
                    availableQuantity: available,
                    checked: true,
                    quantity: existingItem ? Number(existingItem.quantity).toFixed(2) : Math.max(0, remainingGross - stockUse).toFixed(2),
                    stockUse: stockUse.toFixed(2),
                    unitPrice: existingItem ? Number(existingItem.unitPrice).toFixed(2) : '',
                }
            })

            // Itens que já estavam salvos no pedido mas não fazem parte da necessidade calculada
            // (buildPurchaseSummary) — foram adicionados manualmente numa edição anterior. Sem
            // isso, reabrir pra editar e salvar de novo apagaria esses itens silenciosamente, já
            // que o submit() reconstrói tudo a partir de `rows`.
            const manualRows = []
            if (editingPO) {
                ;(editingPO.items || []).forEach((it) => {
                    const materialId = it.materialType === 'aviamento' ? (it.aviamento?._id || it.aviamento) : (it.cloth?._id || it.cloth)
                    if (!materialId) return
                    if (consumedKeys.has(`${it.materialType}_${materialId}`)) return

                    const providerId = it.materialType === 'cloth' ? editingPO.provider?._id : undefined
                    const key = saldoKey(it.materialType, materialId, providerId, it.unit?.value)
                    const ownReserved = ownReservedMap[key] || 0
                    const available = (map[key] || 0) + ownReserved

                    manualRows.push({
                        type: it.materialType,
                        clothId: it.materialType === 'cloth' ? materialId : undefined,
                        clothName: it.materialType === 'cloth' ? it.cloth?.name : undefined,
                        aviamentoId: it.materialType === 'aviamento' ? materialId : undefined,
                        aviamentoName: it.materialType === 'aviamento' ? it.aviamento?.name : undefined,
                        providerId,
                        providerName: it.materialType === 'cloth' ? editingPO.provider?.name : undefined,
                        colorLabel: it.colorLabel || 'Adicionado manualmente',
                        unit: it.unit,
                        grossQuantity: 0,
                        availableQuantity: available,
                        checked: true,
                        quantity: Number(it.quantity).toFixed(2),
                        stockUse: Math.min(available, ownReserved).toFixed(2),
                        unitPrice: Number(it.unitPrice).toFixed(2),
                        manual: true,
                    })
                })
            }

            setRows([...grossRows, ...manualRows])

            setLoad(true)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    function refreshProviders() {
        api.get('/product/getInfoSelect', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            .then((response) => setProviders(response.data.providers || []))
        setModalProvider(false)
    }

    function toggleRow(index) {
        setRows((prev) => prev.map((r, i) => i === index ? { ...r, checked: !r.checked } : r))
    }

    function updateRow(index, field, value) {
        setRows((prev) => prev.map((r, i) => i === index ? { ...r, [field]: value } : r))
    }

    // Formata "Valor unit." como dinheiro (2 casas) só ao sair do campo — em onChange
    // atrapalharia a digitação (ex: forçaria "1.00" antes da pessoa terminar de digitar "12,50").
    function blurUnitPrice(index, value) {
        updateRow(index, 'unitPrice', NumberToMoney(value))
    }

    // Editar "usar do estoque" reajusta a sugestão de "a comprar" (bruta - usado), mas "a comprar"
    // continua livre pra edição manual depois — não há sincronização no sentido contrário. Trava
    // em availableQuantity: usar mais do que existe deixaria a sugestão de compra menor do que a
    // necessidade real, sem reservar de fato o excedente (que nem existe em estoque). Item manual
    // não tem "necessidade bruta" (é sempre 0) — reajustar a compra em cima disso zeraria o que a
    // pessoa acabou de digitar, então só recalcula pros itens vindos do cálculo do pedido.
    function updateStockUse(index, value) {
        setRows((prev) => prev.map((r, i) => {
            if (i !== index) return r
            const use = Math.min(r.availableQuantity, Math.max(0, parseFloat(value) || 0))
            if (r.manual) {
                return { ...r, stockUse: value === '' ? '' : use.toFixed(2) }
            }
            const suggestedBuy = Math.max(0, r.grossQuantity - use)
            return { ...r, stockUse: value === '' ? '' : use.toFixed(2), quantity: suggestedBuy.toFixed(2) }
        }))
    }

    function removeRow(index) {
        setRows((prev) => prev.filter((_, i) => i !== index))
    }

    // Tecidos que o fornecedor escolhido pra este Pedido de Compra tem regra de compra
    // cadastrada — mesmo filtro usado em FormProduct.js, a unidade do item manual vem sempre
    // dessa regra (nunca escolhida solta), pra casar certo com o saldo de Estoque.
    const availableCloths = providerSelected
        ? cloths
            .map((c) => ({ cloth: c, rule: findClothPurchaseRule(providerSelected, c._id) }))
            .filter((x) => x.rule)
            .map((x) => ({ ...x.cloth, label: `${x.cloth.name} (${x.rule.unit.label})`, _rule: x.rule }))
        : []

    function openAddItem() {
        setManualType('cloth')
        setManualCloth(null)
        setManualAviamento(null)
        setManualQuantity('')
        setManualUnitPrice('')
        setAddItemModal(true)
    }

    function confirmAddItem() {
        const qty = parseFloat(manualQuantity) || 0
        if (qty <= 0) {
            setFlashMessage('Informe uma quantidade válida', 'error')
            return
        }

        let newRow
        if (manualType === 'cloth') {
            if (!manualCloth) {
                setFlashMessage('Selecione o tecido', 'error')
                return
            }
            const unit = manualCloth._rule.unit
            const key = saldoKey('cloth', manualCloth._id, providerSelected._id, unit.value)
            newRow = {
                type: 'cloth',
                clothId: manualCloth._id,
                clothName: manualCloth.name,
                providerId: providerSelected._id,
                providerName: providerSelected.name,
                colorLabel: 'Adicionado manualmente',
                unit,
                grossQuantity: 0,
                availableQuantity: stockSaldoMap[key] || 0,
                manual: true,
            }
        } else {
            if (!manualAviamento) {
                setFlashMessage('Selecione o aviamento', 'error')
                return
            }
            const key = saldoKey('aviamento', manualAviamento._id, null, manualAviamento.unit.value)
            newRow = {
                type: 'aviamento',
                aviamentoId: manualAviamento._id,
                aviamentoName: manualAviamento.name,
                unit: manualAviamento.unit,
                grossQuantity: 0,
                availableQuantity: stockSaldoMap[key] || 0,
                manual: true,
            }
        }

        setRows((prev) => [...prev, {
            ...newRow,
            checked: true,
            quantity: qty.toFixed(2),
            stockUse: '0.00',
            unitPrice: manualUnitPrice,
        }])
        setAddItemModal(false)
    }

    const totalValue = useMemo(() => {
        return rows.reduce((acc, r) => {
            if (!r.checked) return acc
            return acc + (parseFloat(r.quantity) || 0) * (parseFloat(r.unitPrice) || 0)
        }, 0)
    }, [rows])

    async function submit() {
        if (!providerSelected) {
            setFlashMessage('Selecione o fornecedor', 'error')
            return
        }

        const items = rows.filter((r) => r.checked && parseFloat(r.quantity) > 0).map((r) => ({
            materialType: r.type,
            cloth: r.type === 'cloth' ? r.clothId : undefined,
            aviamento: r.type === 'aviamento' ? r.aviamentoId : undefined,
            colorLabel: r.colorLabel,
            quantity: parseFloat(r.quantity),
            unit: r.unit,
            unitPrice: parseFloat(r.unitPrice) || 0,
        }))

        if (items.length === 0) {
            setFlashMessage('Selecione ao menos um item', 'error')
            return
        }

        // Reserva no Estoque exatamente o que a pessoa marcou em "Usar do estoque" (capado pelo
        // disponível) — trava esse material pra este pedido, pra não ser contado como disponível
        // por outro Pedido de Compra. Independe de "checked": mesmo um item desmarcado por já ter
        // estoque suficiente pode usar estoque sem gerar item de compra.
        const stockCoverage = rows.map((r) => {
            const covered = Math.min(r.availableQuantity, parseFloat(r.stockUse) || 0)
            if (covered <= 0) return null
            return {
                materialType: r.type,
                cloth: r.type === 'cloth' ? r.clothId : undefined,
                aviamento: r.type === 'aviamento' ? r.aviamentoId : undefined,
                provider: r.type === 'cloth' ? r.providerId : undefined,
                unit: r.unit,
                quantity: covered,
            }
        }).filter(Boolean)

        const body = {
            providerId: providerSelected._id,
            items,
            stockCoverage,
            expectedArrival: expectedArrival || undefined,
            obs,
        }
        if (!editingPO) {
            body.orderId = order._id
            body.installments = parseInt(installments) || 1
        }

        let msgType = 'success'
        const request = editingPO
            ? api.patch(`/purchase/order/edit/${editingPO._id}`, body, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            : api.post('/purchase/order/new', body, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
        const data = await request
            .then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            handleOnSuccess()
        }
    }

    if (!load) {
        return <div className="pg-loading">Carregando resumo de compra...</div>
    }

    return (
        <FormLayout
            title={`${editingPO ? 'Editar' : 'Novo'} Pedido de Compra — #${order.code}`}
            actions={
                <>
                    <Button variant={editingPO ? 'save' : 'new'} onClick={submit}>{editingPO ? 'Salvar Alterações' : 'Criar Pedido de Compra'}</Button>
                    <Button variant="cancel" onClick={btCancelAction}>Cancelar</Button>
                </>
            }
        >
            <div className="po-new">
            <p className="po-new-subtitle">Quantidade sugerida já desconta o que está em outro Pedido de Compra deste pedido. Se quiser aproveitar o que já existe em Estoque, informe quanto em "Usar do estoque" — o valor a comprar é ajustado automaticamente.</p>

            <div className="fl-row">
                <div className="fl-col-wide form-group">
                    <label className="input-label">Fornecedor:
                        {myPermissionProviderCreate && (
                            <div onClick={() => setModalProvider(true)}>
                                <div className="plus-sinal">
                                    <div className="plus-sinal-tip"></div>
                                    <div className="plus-sinal-middle"></div>
                                    <div className="plus-sinal-tip"></div>
                                </div>
                            </div>
                        )}
                    </label>
                    <Select
                        options={providers}
                        getOptionLabel={(p) => p.name}
                        getOptionValue={(p) => p._id}
                        styles={selectStyles}
                        onChange={setProviderSelected}
                        value={providerSelected}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        placeholder="Selecione o fornecedor..."
                    />
                </div>
                <div className="fl-col-narrow form-group">
                    <Input text="Previsão de Chegada" type="date" name="expectedArrival" handleOnChange={(e) => setExpectedArrival(e.target.value)} value={expectedArrival} />
                </div>
                {!editingPO && (
                    <div className="fl-col-narrow form-group">
                        <Input text="Parcelas (Contas a Pagar)" type="number" name="installments" placeholder="1" handleOnChange={(e) => setInstallments(e.target.value)} value={installments} />
                    </div>
                )}
            </div>

            <div className="po-item-list">
                {rows.map((r, i) => (
                    <div key={i} className={`po-item-row${r.checked ? '' : ' po-item-row-disabled'}`}>
                        <Checkbox check={r.checked} handleOnChange={() => toggleRow(i)} />
                        <div className="po-item-info">
                            {r.type === 'aviamento' ? (
                                <span className="po-item-name">{r.aviamentoName} <span className="po-item-tag">— {r.manual ? 'Aviamento adicionado manualmente' : 'Aviamento'}</span></span>
                            ) : (
                                <span className="po-item-name">{r.clothName} <span className="po-item-tag">— {r.colorLabel}</span></span>
                            )}
                            {r.providerName && <span className="po-item-provider">{r.providerName}</span>}
                            <span className="po-item-need">
                                {r.manual ? (
                                    <>a comprar: {r.grossQuantity.toFixed(2)} {r.unit?.value}</>
                                ) : (
                                    <>
                                        ainda falta comprar: {r.grossQuantity.toFixed(2)} {r.unit?.value}
                                        {r.otherCommitted > 0 && <> (necessidade total: {r.originalGrossQuantity.toFixed(2)} {r.unit?.value}, já em outro pedido de compra: {r.otherCommitted.toFixed(2)} {r.unit?.value})</>}
                                    </>
                                )}
                                {' · '}disponível em estoque: {r.availableQuantity.toFixed(2)} {r.unit?.value}
                            </span>
                        </div>
                        <div className="po-item-stockuse">
                            <Input text={`Usar do estoque (máx. ${r.availableQuantity.toFixed(2)})`} type="number" name="stockUse" placeholder="0.00" handleOnChange={(e) => updateStockUse(i, e.target.value)} value={r.stockUse} />
                        </div>
                        <div className="po-item-qty">
                            <Input text="A comprar" type="number" name="quantity" placeholder="0.00" handleOnChange={(e) => updateRow(i, 'quantity', e.target.value)} value={r.quantity} disabled={!r.checked} />
                        </div>
                        <div className="po-item-price">
                            <Input text="Valor unit. (R$)" type="number" name="unitPrice" placeholder="0.00" handleOnChange={(e) => updateRow(i, 'unitPrice', e.target.value)} handleOnBlur={(e) => blurUnitPrice(i, e.target.value)} value={r.unitPrice} disabled={!r.checked} />
                        </div>
                        {r.manual && (
                            <FaTrashAlt className="shd-icon-row-trash" title="Remover item" onClick={() => removeRow(i)} />
                        )}
                    </div>
                ))}
                {rows.length === 0 && (
                    <p className="po-item-empty">Nenhum item com consumo de tecido ou aviamento neste pedido.</p>
                )}
            </div>

            <div className="po-item-add">
                <button type="button" className="po-select-btn" onClick={openAddItem}>
                    <FaPlus size={14} /><span>Adicionar item</span>
                </button>
            </div>

            <div className="form-group">
                <Input text="Observação" type="text" name="obs" placeholder="Observações sobre o pedido de compra" handleOnChange={(e) => setObs(e.target.value)} value={obs} />
            </div>

            <div className="po-new-footer">
                <span className="po-new-total">Total: R$ {totalValue.toFixed(2)}</span>
            </div>

            <Modal
                modalStatus={modalProvider}
                changeStatus={setModalProvider}
                w={80}
                content={
                    <NewProvider btCancelAction={() => setModalProvider(false)} handleOnSuccess={refreshProviders} />
                }
            />

            <Modal
                modalStatus={addItemModal}
                changeStatus={() => setAddItemModal(false)}
                w={45}
                content={
                    <FormLayout
                        title="Adicionar item"
                        actions={
                            <>
                                <Button variant="new" onClick={confirmAddItem}>Adicionar</Button>
                                <Button variant="cancel" onClick={() => setAddItemModal(false)}>Cancelar</Button>
                            </>
                        }
                    >
                        <div className="po-additem">
                        <div className="form-group">
                            <label className="input-label">Tipo:</label>
                            <Select
                                options={MANUAL_TYPE_OPTIONS}
                                styles={selectStyles}
                                value={MANUAL_TYPE_OPTIONS.find((o) => o.value === manualType)}
                                onChange={(opt) => { setManualType(opt.value); setManualCloth(null); setManualAviamento(null) }}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>

                        {manualType === 'cloth' ? (
                            !providerSelected ? (
                                <p className="po-additem-warning">Selecione o fornecedor do pedido antes de adicionar um tecido.</p>
                            ) : availableCloths.length === 0 ? (
                                <p className="po-additem-warning">Esse fornecedor não tem nenhum tecido com regra de compra cadastrada.</p>
                            ) : (
                                <div className="form-group">
                                    <label className="input-label">Tecido:</label>
                                    <Select
                                        options={availableCloths}
                                        getOptionLabel={(c) => c.label}
                                        getOptionValue={(c) => c._id}
                                        styles={selectStyles}
                                        value={manualCloth}
                                        onChange={setManualCloth}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        placeholder="Selecione o tecido..."
                                    />
                                </div>
                            )
                        ) : (
                            <div className="form-group">
                                <label className="input-label">Aviamento:</label>
                                <Select
                                    options={aviamentos}
                                    getOptionLabel={(a) => `${a.name} (${a.unit?.label || a.unit?.value})`}
                                    getOptionValue={(a) => a._id}
                                    styles={selectStyles}
                                    value={manualAviamento}
                                    onChange={setManualAviamento}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    placeholder="Selecione o aviamento..."
                                />
                            </div>
                        )}

                        <div className="fl-row">
                            <div className="form-group">
                                <Input text="Quantidade a comprar" type="number" name="manualQuantity" placeholder="0.00" handleOnChange={(e) => setManualQuantity(e.target.value)} value={manualQuantity} />
                            </div>
                            <div className="form-group">
                                <Input text="Valor unit. (R$)" type="number" name="manualUnitPrice" placeholder="0.00" handleOnChange={(e) => setManualUnitPrice(e.target.value)} handleOnBlur={(e) => setManualUnitPrice(NumberToMoney(e.target.value))} value={manualUnitPrice} />
                            </div>
                        </div>
                        </div>
                    </FormLayout>
                }
            />
            </div>
        </FormLayout>
    )
}

export default NewPurchaseOrder
